import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";


export default function Waitlist() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post("/api/waitlist", {
        email,
        role: role || null,
      });
      setDone(true);
      toast.success("Du bist auf der Liste.", {
        description: "Wir melden uns, sobald BeParent verfügbar ist.",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Etwas ist schiefgelaufen. Versuche es erneut.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative py-28 md:py-40"
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="relative overflow-hidden rounded-[36px] border border-white/8 bg-gradient-to-br from-[#152036] via-[#0F172A] to-[#0B1120] p-10 md:p-16">
          {/* glow */}
          <div
            aria-hidden
            className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(0,250,248,0.16), transparent 60%)",
              filter: "blur(40px)",
            }}
          />
          <div className="absolute inset-0 bp-grid opacity-40" aria-hidden />

          <div className="relative">
            <div className="flex items-center gap-4 mb-8">
              <span className="cyan-dot" />
              <span className="font-mono-bp text-[11px] tracking-[0.35em] uppercase text-[#00FAF8]">
                Early Access · 2026
              </span>
            </div>

            <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.03em] font-medium max-w-3xl">
              Sei unter den ersten, die BeParent nutzen.
            </h2>
            <p className="mt-6 text-white/60 text-lg max-w-2xl leading-relaxed">
              Kein Newsletter, keine Werbung. Nur eine Nachricht, sobald die App
              verfügbar ist.
            </p>

            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-10 inline-flex items-center gap-3 rounded-full border border-[#00FAF8]/40 bg-[#00FAF8]/10 px-5 py-3"
                data-testid="waitlist-success"
              >
                <Check size={16} className="text-[#00FAF8]" />
                <span className="text-white/90">Du bist auf der Liste — danke.</span>
              </motion.div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="waitlist-form"
                className="mt-10 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 max-w-2xl"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="deine@e-mail.de"
                    data-testid="waitlist-email"
                    className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-4 text-white placeholder:text-white/30 outline-none focus:border-[#00FAF8]/60 focus:bg-white/[0.06] transition-colors"
                  />
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    data-testid="waitlist-role"
                    className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-4 text-white outline-none focus:border-[#00FAF8]/60 transition-colors appearance-none"
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg fill='%2394A3B8' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'><path d='M5.5 8l4.5 4.5L14.5 8z'/></svg>\")",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 1rem center",
                    }}
                  >
                    <option value="" className="bg-[#0F172A]">
                      Rolle (optional)
                    </option>
                    <option value="Elternteil" className="bg-[#0F172A]">Elternteil</option>
                    <option value="Anwalt" className="bg-[#0F172A]">Anwalt</option>
                    <option value="Jugendamt" className="bg-[#0F172A]">Jugendamt</option>
                    <option value="Verfahrensbeistand" className="bg-[#0F172A]">Verfahrensbeistand</option>
                    <option value="Mediator" className="bg-[#0F172A]">Mediator:in</option>
                    <option value="Gericht" className="bg-[#0F172A]">Gericht</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="waitlist-submit"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#00FAF8] px-7 py-4 text-[15px] font-medium text-[#0F172A] disabled:opacity-60 hover:shadow-[0_0_40px_rgba(0,250,248,0.5)] transition-shadow"
                >
                  {loading ? "Bitte warten…" : "Zur Warteliste"}
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </button>
              </form>
            )}

            <p className="mt-6 text-xs text-white/35 font-mono-bp">
              Deine E-Mail wird ausschließlich für diesen Zweck genutzt. Kein Tracking.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
